import React from 'react';
import { useI18n } from '../../i18n/I18nContext';

/**
 * ProgressBar — the CRM's one progress bar (`.ui-progress`, styles/ui.css): yellow while in
 * progress, green once it is complete. It replaces the page-local bars (.ol-bar, .pc-uses-bar,
 * .stats-progress-*, .discount-usage-*, .td-progress ...).
 *
 *   <ProgressBar value={67} label="English for Beginners" />
 *   <ProgressBar value={used} max={limit} size="sm" tone="info" />
 *
 * Props: value, max (default 100), label (names the bar for screen readers; defaults to
 * "Progress"), size ('sm' 6px | 'md' 8px | 'lg' 12px), tone (a data tone instead of yellow:
 * 'info' | 'success' | 'teal' | 'coral' | 'purple' ...), complete (force or forbid the green state;
 * by default it is on at 100%), className. The value is clamped to 0..max; a missing or non-numeric
 * value reads as 0.
 */
function ProgressBar({ value, max = 100, label, size = 'md', tone, complete, className = '' }) {
  const { t } = useI18n();
  const top = Number(max) > 0 ? Number(max) : 100;
  const raw = Number(value);
  const now = Number.isFinite(raw) ? Math.min(top, Math.max(0, raw)) : 0;
  const percent = (now / top) * 100;
  const done = complete === undefined ? percent >= 100 : Boolean(complete);

  const cls = [
    'ui-progress',
    size === 'sm' ? 'ui-progress--sm' : size === 'lg' ? 'ui-progress--lg' : '',
    done ? 'is-complete' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      role="progressbar"
      aria-label={label || t('ui.progress')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
      data-tone={tone || undefined}
    >
      <span className="ui-progress__bar" style={{ width: `${percent}%` }} />
    </div>
  );
}

export default ProgressBar;
